import { useEffect, useRef, useState } from 'react';

/**
 * 🐧 企鵝 Mascot：固定右下角
 * - 平時：慢飄、hover 跳一下
 * - 點一下：跳出俏皮話氣泡，氣泡含「看今日報告」捷徑
 */

const QUOTES = [
  '今天先看大盤臉色再決定要不要追單～',
  '聽說外資悄悄加碼 TSMC 喔！',
  'KD 黃金交叉，準備啟動？',
  '別追高，等回測支撐再進場 🐧',
  '量先價行，量沒出來別亂出手',
  '看不懂就空手，現金也是部位！',
  '美股財報週小心跳空，停損先設好',
  '台股盤前先掃一下三大法人',
  '盤中亂、盤後想，給自己冷靜時間',
  '今天的進場理由還在嗎？沒了就出場別凹',
];

const FIRST_QUOTE = QUOTES[0] ?? '街鵝上班中 🐧';

function pickQuote(prev: string) {
  if (QUOTES.length <= 1) return FIRST_QUOTE;
  let q = prev;
  while (q === prev) {
    q = QUOTES[Math.floor(Math.random() * QUOTES.length)] ?? FIRST_QUOTE;
  }
  return q;
}

function scrollToDailyReport() {
  const anchor = document.getElementById('daily-report-anchor');
  if (!anchor) return;
  // 若摺疊區是收的，先展開（CollapsibleSection 收起時 chevron 是 ChevronRight，展開時是 ChevronDown）
  const header = anchor.querySelector('section.card > header') as HTMLElement | null;
  const isCollapsed = header?.querySelector('svg.lucide-chevron-right') != null;
  if (isCollapsed) header?.click();
  requestAnimationFrame(() => {
    anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

export function Mascot() {
  const [open, setOpen] = useState(false);
  const [quote, setQuote] = useState<string>(() => FIRST_QUOTE);
  const wrapRef = useRef<HTMLDivElement>(null);

  function handleClick() {
    setOpen((o) => {
      const next = !o;
      if (next) setQuote((q) => pickQuote(q));
      return next;
    });
  }

  // 點氣泡外面就收起來
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div ref={wrapRef} className="fixed bottom-4 right-4 z-50 select-none">
      {open && (
        <div className="absolute bottom-16 right-0 w-56 rounded-xl border border-(--border) bg-(--bg-soft) shadow-lg p-3 text-xs">
          <div className="text-(--text-primary) leading-relaxed">{quote}</div>
          <div className="mt-2 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                scrollToDailyReport();
                setOpen(false);
              }}
              className="text-[11px] px-2 py-1 rounded-md bg-(--accent)/15 text-(--accent) hover:bg-(--accent)/25 transition-colors"
            >
              看今日報告 →
            </button>
            <button
              type="button"
              onClick={() => setQuote((q) => pickQuote(q))}
              className="text-[10px] text-(--text-muted) hover:text-(--text-primary)"
              aria-label="換一句"
            >
              再來一句 🔄
            </button>
          </div>
          {/* 小尾巴指向企鵝 */}
          <div className="absolute -bottom-1 right-6 w-2 h-2 rotate-45 bg-(--bg-soft) border-r border-b border-(--border)" />
        </div>
      )}
      <button
        type="button"
        onClick={handleClick}
        title="點我說句話 🐧"
        className="relative cursor-pointer group bg-transparent border-0 p-0"
        aria-label="企鵝小幫手"
      >
        <div className="absolute inset-0 bg-(--accent)/20 blur-2xl rounded-full pointer-events-none" />
        <div className="relative text-4xl animate-mascot group-hover:animate-mascot-bounce">🐧</div>
        <span className="absolute -top-1 -right-1 text-sm animate-pulse-soft">✨</span>
      </button>
    </div>
  );
}
